/* ============================================================
   MOONRISE — Product media gallery
   Custom element: <product-media-gallery>
   Reference behavior: moon-rise-framer/script.js lines 665-761
     - Thumb click swaps main image (and syncs is-active class)
     - Prev/next arrows wrap around index
     - Vertical thumb scroller (desktop), horizontal CSS scroll-snap (mobile)
     - Up/Down nav buttons step the track by one thumb stride
     - ResizeObserver syncs --thumbs-max-h to main image height (desktop only)
     - Zoom button opens the current image in a native <dialog> lightbox
   No external deps. Defer-loaded by layout/theme.liquid.
   ============================================================ */

(function () {
  'use strict';

  if (customElements.get('product-media-gallery')) return;

  var MOBILE_BP = 768;

  function isMobile() {
    return window.innerWidth <= MOBILE_BP;
  }

  function ensureLightbox() {
    var existing = document.getElementById('moonrise-image-lightbox');
    if (existing) return existing;

    var dialog = document.createElement('dialog');
    dialog.id = 'moonrise-image-lightbox';
    dialog.className = 'moonrise-image-lightbox';
    dialog.innerHTML =
      '<button type="button" class="moonrise-image-lightbox__close" data-lightbox-close aria-label="Close">' +
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" width="24" height="24" focusable="false">' +
          '<path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/>' +
        '</svg>' +
      '</button>' +
      '<img class="moonrise-image-lightbox__img" alt="" />';
    document.body.appendChild(dialog);

    dialog.addEventListener('click', function (e) {
      if (e.target === dialog || e.target.closest('[data-lightbox-close]')) {
        dialog.close();
      }
    });
    return dialog;
  }

  class ProductMediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.thumbs = Array.from(this.querySelectorAll('.product-thumb'));
      this.mainImg = this.querySelector('[data-gallery-main-img]');
      this.mainWrap = this.querySelector('[data-gallery-main]');
      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      this.thumbsUpBtn = this.querySelector('[data-gallery-prev-thumb]');
      this.thumbsDownBtn = this.querySelector('[data-gallery-next-thumb]');
      this.zoomBtn = this.querySelector('[data-gallery-zoom]');

      this._currentIndex = Math.max(0, this.thumbs.findIndex(function (t) {
        return t.classList.contains('is-active');
      }));

      this._bindThumbs();
      this._bindArrows();
      this._bindThumbScroll();
      this._bindZoom();
      this._bindVariantSync();
      this._observeMain();
    }

    /* ---------- Thumb click → swap main ---------- */
    _bindThumbs() {
      var self = this;
      this.thumbs.forEach(function (thumb, index) {
        thumb.addEventListener('click', function () {
          self.setActive(index);
        });
      });
    }

    setActive(index) {
      if (!this.thumbs.length) return;
      if (index < 0) index = this.thumbs.length - 1;
      if (index >= this.thumbs.length) index = 0;
      this._currentIndex = index;

      var thumb = this.thumbs[index];
      var src = thumb.getAttribute('data-img');
      if (src && this.mainImg) {
        // Browsers prefer `srcset` over `src` when both are present.
        // The initial main image ships with a srcset of the FIRST media's
        // widths — if we only update src, the browser keeps rendering the
        // original image from srcset. Strip srcset/sizes so the new src wins.
        this.mainImg.removeAttribute('srcset');
        this.mainImg.removeAttribute('sizes');
        this.mainImg.src = src;
        var alt = thumb.getAttribute('aria-label');
        if (alt) this.mainImg.alt = alt;
        var mediaId = thumb.getAttribute('data-media-id');
        if (mediaId) this.mainImg.setAttribute('data-media-id', mediaId);
      }
      this.thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === index);
      });
    }

    /* ---------- Prev / Next arrow buttons ---------- */
    _bindArrows() {
      var self = this;
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () {
        self.setActive(self._currentIndex - 1);
      });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () {
        self.setActive(self._currentIndex + 1);
      });
    }

    /* ---------- Thumb track scroller (up/down nav) ---------- */
    _bindThumbScroll() {
      if (!this.track || !this.thumbsUpBtn || !this.thumbsDownBtn) return;
      var self = this;

      function getStep() {
        var firstThumb = self.track.querySelector('.product-thumb');
        if (!firstThumb) return 96;
        var rect = firstThumb.getBoundingClientRect();
        return (isMobile() ? rect.width : rect.height) + 12;
      }

      function updateNavState() {
        if (isMobile()) {
          self.thumbsUpBtn.disabled = self.track.scrollLeft <= 1;
          self.thumbsDownBtn.disabled =
            self.track.scrollLeft + self.track.clientWidth >= self.track.scrollWidth - 1;
        } else {
          self.thumbsUpBtn.disabled = self.track.scrollTop <= 1;
          self.thumbsDownBtn.disabled =
            self.track.scrollTop + self.track.clientHeight >= self.track.scrollHeight - 1;
        }
      }

      this.thumbsUpBtn.addEventListener('click', function () {
        var step = getStep();
        if (isMobile()) self.track.scrollBy({ left: -step, behavior: 'smooth' });
        else self.track.scrollBy({ top: -step, behavior: 'smooth' });
      });

      this.thumbsDownBtn.addEventListener('click', function () {
        var step = getStep();
        if (isMobile()) self.track.scrollBy({ left: step, behavior: 'smooth' });
        else self.track.scrollBy({ top: step, behavior: 'smooth' });
      });

      this.track.addEventListener('scroll', updateNavState, { passive: true });
      window.addEventListener('resize', updateNavState);
      updateNavState();
      this._updateNavState = updateNavState;
    }

    /* ---------- ResizeObserver: thumb height matches main image ---------- */
    _observeMain() {
      if (!this.mainWrap || typeof ResizeObserver !== 'function') return;
      var self = this;

      function sync() {
        if (!self.thumbsContainer) {
          self.thumbsContainer = self.querySelector('.product-gallery-thumbs');
        }
        if (!self.thumbsContainer) return;
        if (isMobile()) {
          self.thumbsContainer.style.setProperty('--thumbs-max-h', '');
          return;
        }
        var mainH = self.mainWrap.offsetHeight;
        var upH = self.thumbsUpBtn ? self.thumbsUpBtn.offsetHeight || 36 : 36;
        var downH = self.thumbsDownBtn ? self.thumbsDownBtn.offsetHeight || 36 : 36;
        var trackMax = Math.max(160, mainH - upH - downH - 20);
        self.thumbsContainer.style.setProperty('--thumbs-max-h', trackMax + 'px');
        if (self._updateNavState) self._updateNavState();
      }

      this._sync = sync;
      var ro = new ResizeObserver(sync);
      ro.observe(this.mainWrap);
      this._resizeObserver = ro;

      if (this.mainImg) this.mainImg.addEventListener('load', sync);
      window.addEventListener('resize', sync);
      window.addEventListener('load', sync);
      sync();
    }

    /* ---------- Zoom button → native <dialog> lightbox ---------- */
    _bindZoom() {
      if (!this.zoomBtn || !this.mainImg) return;
      var self = this;
      this.zoomBtn.addEventListener('click', function () {
        var dialog = ensureLightbox();
        var img = dialog.querySelector('.moonrise-image-lightbox__img');
        img.src = self.mainImg.src;
        img.alt = self.mainImg.alt || '';
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      });
    }

    /* ---------- Public API for <product-form> variant changes ---------- */
    _bindVariantSync() {
      var self = this;
      this.addEventListener('product:variant-media', function (e) {
        var mediaId = e && e.detail && e.detail.mediaId;
        if (!mediaId) return;
        var idx = self.thumbs.findIndex(function (t) {
          return t.getAttribute('data-media-id') === String(mediaId);
        });
        if (idx >= 0) self.setActive(idx);
      });
    }

    disconnectedCallback() {
      if (this._resizeObserver) this._resizeObserver.disconnect();
    }
  }

  customElements.define('product-media-gallery', ProductMediaGallery);
})();
